/**
 * DTA API service. Uses the generated SDK for request construction (typed path/query params,
 * automatic auth). Uses raw responses to preserve backend snake_case field names that consumers
 * depend on.
 */

export type { ParsedFormResult } from "@/generated/sdk"
import type {
  DTARecord,
  DTADetails,
  DTAListResponse,
  DTAVersion,
  SigningRecord,
  VtmfFilingJob,
  CreateDTARequest,
  CreateDTAResponse,
} from "../types/dta"
import { uploadFileToS3 } from "../utils/uploadFileToS3"
import { sdk, sdkCall } from "./sdk"

import type {
  SigningRecordsResponse,
  VtmfFilingsResponse,
  DtaSuggestionsResponse,
  DTAVersionsResponse,
  DtaListResponse,
  VersionActionResponse,
  DtaStageResponse,
  UpdateDtaRequest,
  CreateDtaVersionRequest,
  ParseSpecResponse,
} from "@/generated/sdk"

// Re-export types for consumers
export type { DTARecord, DTADetails, SigningRecord, VtmfFilingJob }
export * from "../types/dta"

/**
 * Extract JSON body from a raw SDK response.
 */
export const dtaApiService = {
  listAllDTAs: async (options?: {
    limit?: number
    nextToken?: string
    status?: string
  }): Promise<DtaListResponse> => {
    return sdkCall(sdk.listAllDtas(options))
  },

  getStudyDTAs: async (
    studyId: string,
    options?: {
      status?: string
      vendor?: string
      domain?: string
      page?: number
      limit?: number
      sort_by?: string
      sort_order?: "asc" | "desc"
      nextToken?: string
    },
  ): Promise<DTAListResponse> => {
    return sdkCall(
      sdk.listStudyDtas({
        studyId,
        status: options?.status,
        vendor: options?.vendor,
        domain: options?.domain,
        page: options?.page,
        limit: options?.limit,
        sortBy: options?.sort_by,
        sortOrder: options?.sort_order,
        nextToken: options?.nextToken,
      }),
    )
  },

  getDTADetails: async (dtaId: string, version?: string): Promise<DTADetails> => {
    return sdkCall(sdk.getDtaDetail({ dtaId, version }))
  },

  getSigningRecords: async (dtaId: string): Promise<SigningRecordsResponse> => {
    return sdkCall(sdk.getDtaSigningRecords({ dtaId }))
  },

  signDTA: async (
    dtaId: string,
    payload: { method?: string; attestation_version?: string; comment?: string },
  ): Promise<unknown> => {
    return sdkCall(sdk.signDta({ dtaId, signDtaRequest: payload }))
  },

  createDTA: async (studyId: string, request: CreateDTARequest): Promise<CreateDTAResponse> => {
    return sdkCall(
      sdk.createDta({
        studyId,
        createDtaRequest: request,
      }),
    )
  },

  updateDTA: async (dtaId: string, request: UpdateDtaRequest): Promise<DTADetails> => {
    return sdkCall(
      sdk.updateDta({
        dtaId,
        updateDtaRequest: request,
      }),
    )
  },

  getVtmfFilings: async (dtaId: string): Promise<VtmfFilingsResponse> => {
    return sdkCall(sdk.getDtaVtmfFilings({ dtaId }))
  },

  triggerVtmfFiling: async (
    dtaId: string,
    payload?: { idempotency_key?: string },
  ): Promise<unknown> => {
    return sdkCall(sdk.triggerDtaVtmfFiling({ dtaId, triggerVtmfFilingRequest: payload || {} }))
  },

  getThinkingSteps: async (dtaId: string) => {
    return sdkCall(sdk.getDtaThinkingSteps({ dtaId }))
  },

  getVendorDTAs: async (options?: {
    limit?: number
    nextToken?: string
  }): Promise<DTAListResponse> => {
    return sdkCall(
      sdk.listAllDtas({
        limit: options?.limit,
        nextToken: options?.nextToken,
      }),
    )
  },

  getDataQualitySuggestions: async (dtaId: string): Promise<DtaSuggestionsResponse> => {
    return sdkCall(sdk.getDtaSuggestions({ dtaId }))
  },

  listVersions: async (dtaId: string): Promise<DTAVersionsResponse> => {
    return sdkCall(sdk.listDtaVersions({ dtaId }))
  },

  createVersion: async (dtaId: string, data: CreateDtaVersionRequest): Promise<DTAVersion> => {
    return sdkCall(
      sdk.createDtaVersion({
        dtaId,
        createDtaVersionRequest: data,
      }),
    )
  },

  getVersion: async (dtaId: string, version: string): Promise<DTAVersion> => {
    return sdkCall(sdk.getDtaVersion({ dtaId, version }))
  },

  activateVersion: async (dtaId: string, version: string): Promise<VersionActionResponse> => {
    return sdkCall(sdk.activateDtaVersion({ dtaId, version, body: {} }))
  },

  deactivateVersion: async (dtaId: string, version: string): Promise<VersionActionResponse> => {
    return sdkCall(sdk.deactivateDtaVersion({ dtaId, version, body: {} }))
  },

  deactivateDTA: async (dtaId: string, version?: string): Promise<DtaStageResponse> => {
    return sdkCall(
      sdk.deactivateDta({
        dtaId,
        deactivateDtaRequestBody: { version },
      }),
    )
  },

  reactivateDTA: async (dtaId: string, version?: string): Promise<DtaStageResponse> => {
    return sdkCall(
      sdk.reactivateDta({
        dtaId,
        reactivateDtaRequestBody: { version },
      }),
    )
  },

  /**
   * Presign a file upload for a DTA (does not confirm the upload - just presigns and uploads to
   * S3). Returns the upload_id for use in createVersion or updateDTA.
   */
  presignAndUploadFile: async (
    studyId: string,
    file: File,
    uploadType: UploadType,
    onProgress?: (percent: number) => void,
  ): Promise<{ upload_id: string }> => {
    const presign = await sdkCall(
      sdk.presignDocumentUpload({
        studyId,
        presignUploadRequest: { file_name: file.name, upload_type: uploadType },
      }),
    )

    await uploadFileToS3(presign.upload_url!, file, presign.content_type!, onProgress)

    return { upload_id: presign.upload_id! }
  },

  /**
   * Parse an EDC file to discover domains/forms. Requires an upload_id from a prior presign+upload.
   */
  parseEdc: async (studyId: string, uploadId: string): Promise<ParseSpecResponse> => {
    const { getApiToken } = await import("./apiToken")
    const token = await getApiToken()
    const res = await fetch(`/api/v1/studies/${studyId}/dtas/parse-edc`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ upload_id: uploadId }),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body.error || body.message || `API Error: ${res.status}`)
    }
    return res.json()
  },

  /**
   * Parse an SDTM mapping specification to discover its domains. Returns the same `forms[]` shape
   * as `parseEdc` (one entry per subject-data SDTM domain). Requires an upload_id from a prior
   * presign+upload.
   */
  parseSdtm: async (studyId: string, uploadId: string): Promise<ParseSpecResponse> => {
    const { getApiToken } = await import("./apiToken")
    const token = await getApiToken()
    const res = await fetch(`/api/v1/studies/${studyId}/dtas/parse-sdtm`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ upload_id: uploadId }),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body.error || body.message || `API Error: ${res.status}`)
    }
    return res.json()
  },
}

export type UploadType = "edc" | "dta" | "sdtm"
export type DtaCreationType = UploadType | "blank"
